import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { ContactNavigated } from '../components/ContactNavigated'
import { Content } from '../components/Content'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { SalesRespresentativesMap } from '../components/SalesRespresentativesMap'
import { Seo } from '../components/Seo'
import { WidthBound } from '../components/WidthBound'
import { usePageLink } from '../src/hooks/usePageLink'
import {
	ContactOfficeFragment,
	VendorFragment,
	VendorType,
	PageFragment,
	SpecialPageType,
} from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { getLinkWithQuery } from '../src/model/urls'
import { axiosGet } from '../src/utils/axiosWrapper'

export type ContactSalesRepresentativesFilters = {
	address: {lat: number, lng: number} | null,
	type: VendorType | null,
	searchQuery: string | null
}

export default function ContactSalesRepresentatives({
	page,
	possibleLocales,
	contactPlaces,
	contactCountries,
	filter
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const router = useRouter();
	const salesRespresentativesMapLink = usePageLink(SpecialPageType.contactSalesRepresentatives)!
	const makeLink = React.useCallback((f: ContactSalesRepresentativesFilters) => {

		const link = {
			...getLinkWithQuery(salesRespresentativesMapLink, f.searchQuery ? { q: f.searchQuery!, lat: f.address?.lat, lng: f.address?.lng} : {
				lat: f.address?.lat, 
				lng: f.address?.lng,
				type: f.type
			}),
			scroll: false,
		};

		return link;
	}, [salesRespresentativesMapLink]);	

	const data = page ? getLocale(page.locales, possibleLocales) : null;
	if (!page || !data) {
		return <MissingContent />
	}

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					},
				]}
			/>
			<Intro title={data.title}>
				<WidthBound>
					<Content content={data.content} innerSize="narrow" />
				</WidthBound>
			</Intro>
			<ContactNavigated
				current="contactSalesRepresentatives"
				offices={contactCountries?.offices || []}
			>
				<SalesRespresentativesMap filter={filter} onFilterChange={(f: ContactSalesRepresentativesFilters) => {
					const link = makeLink(f);
					router.push(link.href, link.as, {shallow: false, scroll: false});
				}} contactPlaces={contactPlaces} />
			</ContactNavigated>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = (ctx.query.locale as Locale) || DEFAULT_LOCALE
	const possibleLocales = getPossibleLocales(locale as Locale)

	const queryParams = ctx.query

	let lat =
		typeof  queryParams.lat === 'string' && queryParams.lat
			? parseFloat(queryParams.lat)
			: null

	let lng =
		typeof queryParams.lng === 'string' && queryParams.lng
			? parseFloat(queryParams.lng)
			: null
	
	if((lat && !lng) || (!lat && lng) || isNaN(lat!) || isNaN(lng!))
	{
		lat = null;
		lng = null;
	}

	const type =
		typeof queryParams.type === 'string' && queryParams.type
			? queryParams.type
			: null

	const q =
		typeof queryParams.q === 'string' && queryParams.q
			? queryParams.q
			: null

	const {
		getPage: page,
		listContactPlace: contactPlaces,
		getContactCountry: contactCountries,
	} = await axiosGet('/vendors/get', {locale: locale, navigationId: ctx.query.id, lat, lng, q: q, type: type}) as {
		getPage: PageFragment,
		listContactPlace: VendorFragment[],
		getContactCountry: {
			offices: ContactOfficeFragment[]
		} 
	};

	return { props: { 
		page, 
		possibleLocales, 
		contactPlaces, 
		contactCountries,
		filter: {
			type,
			address: lat ? {lat: lat, lng: lng} : null,
			searchQuery: q
		} as ContactSalesRepresentativesFilters
	} }
}
